/*
 * Omni Channel reporting readiness — evidence, not layout.
 *
 * Omni Channel = what media was bought. Execution provider = where it ran.
 * More than one provider can deliver the same channel, so a channel is
 * REPORTING READY only when the canonical reporting says its picture is
 * complete for ALL of its known contributing providers — never because one
 * provider happens to be healthy. The backend resolves that
 * (`channelReporting`); nothing here inspects, names or infers a provider.
 *
 * When the canonical payload carries no ruling for a channel (an older
 * backend, the provider fold off, or no delivery evidence in the window) the
 * caller's existing signal is used unchanged.
 */

export function readChannelReporting(payload) {
  const summary = payload?.canonicalReporting || payload?.canonicalSummary || payload?.summary || payload || null;
  const evidence = summary?.channelReporting;
  return evidence && typeof evidence === "object" && !Array.isArray(evidence) ? evidence : null;
}

export function resolveChannelReportingReady(channelReporting, channelKey, fallbackReady) {
  const ruling = channelReporting?.[channelKey];
  return ruling && typeof ruling.ready === "boolean" ? ruling.ready : Boolean(fallbackReady);
}

export function listPartiallyReportingChannels(channelReporting, channelDefinitions = []) {
  return channelDefinitions
    .filter((channel) => {
      const ruling = channelReporting?.[channel.key];
      return Boolean(ruling && (ruling.partial === true || ruling.unavailable === true));
    })
    .map((channel) => ({ key: channel.key, label: channel.label, ruling: channelReporting[channel.key] }));
}

// Client wording stays provider-neutral.
export function describePartialChannelReporting(channels = []) {
  if (!channels.length) return "";
  const labels = channels.map((channel) => channel.label);
  const list = labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
  return channels.every((channel) => channel.ruling?.unavailable === true)
    ? `Reporting for ${list} is currently unavailable.`
    : `Reporting for part of ${list} is currently unavailable.`;
}
